import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { Container, Eyebrow, Display, useReveal } from "./ui";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";
import { testimonials } from "../../pages/verticalContent";

// Switchable pilot testimonials. Pass `page` to show only the ones tagged for that page.

const Wrap = styled.section`
  padding: ${(p) => (p.$compact ? "0 0 120px" : "140px 0")};

  /* New home page (Josh, 29 Sep 2026): the whole section fits one screen on a desktop, with the
     three stories stacked beside the open one. */
  @media ${media.wide} {
    &.fit {
      min-height: 100svh;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 96px 0 20px;
    }
    &.fit .card {
      padding-top: 56px;
    }
    &.fit h2 {
      font-size: clamp(36px, 3.8vw, 56px);
    }
    &.fit .head {
      margin-bottom: 24px;
    }
    &.fit .layout {
      display: grid;
      grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.6fr);
      gap: 16px;
      align-items: stretch;
    }
    &.fit .sel {
      grid-template-columns: minmax(0, 1fr);
      grid-auto-rows: 1fr;
      margin: 0;
    }
    &.fit .card {
      gap: 18px;
      padding: 64px 44px 36px;
    }
    &.fit .pull {
      font-size: clamp(24px, 2.2vw, 32px);
    }
    &.fit .quote {
      margin-top: 14px;
      gap: 10px;
    }
    &.fit .quote p {
      font-size: 16px;
      line-height: 1.55;
    }
    &.fit .who {
      margin-top: 16px;
    }
  }
  /* iPad landscape with Safari's tab bar leaves about 745px: tighten so the disclaimer still fits */
  @media ${media.wide} and (max-height: 800px) {
    &.fit {
      padding: 84px 0 12px;
    }
    &.fit .head {
      margin-bottom: 16px;
    }
    &.fit .card {
      gap: 12px;
      padding: 52px 40px 26px;
    }
    &.fit .pull {
      font-size: clamp(22px, 2vw, 28px);
    }
    &.fit .quote p {
      font-size: 15px;
      line-height: 1.5;
    }
    &.fit .who {
      margin-top: 12px;
    }
    &.fit .sel > button {
      padding: 14px 18px 18px;
    }
  }
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 36px;
`;

const ADVANCE_MS = 10000;

const fillUp = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const Selector = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
  grid-template-columns: minmax(0, 1fr);

  @media ${media.medium} {
    grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  }
`;

const Pick = styled.button`
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 8px;
  text-align: left;
  padding: 18px 20px 22px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$on ? colors.forest : colors.stone)};
  background: ${(p) => (p.$on ? colors.forest : colors.linen)};
  color: ${(p) => (p.$on ? colors.linen : colors.ink)};
  transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.3s ease;

  &:hover {
    border-color: ${colors.forest};
    transform: translateY(-2px);
  }

  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  b {
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 19px;
    letter-spacing: -0.01em;
  }
  .cue {
    flex: none;
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: 4px 8px;
    border-radius: 999px;
    color: ${(p) => (p.$on ? colors.forest : colors.copper)};
    background: ${(p) => (p.$on ? colors.copperLight : "rgba(143, 83, 41, 0.1)")};
  }
  q {
    font-family: ${fonts.display};
    font-style: normal;
    font-weight: 300;
    font-size: 16px;
    line-height: 1.35;
    opacity: 0.8;
  }
  .bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: ${(p) => (p.$on ? "rgba(250,247,241,0.15)" : "transparent")};
  }
  .bar i {
    display: block;
    height: 100%;
    background: ${colors.copperLight};
    transform-origin: 0 50%;
    transform: scaleX(0);
  }
  .bar i.run {
    animation: ${fillUp} ${ADVANCE_MS}ms linear forwards;
  }
  .bar i.full {
    transform: scaleX(1);
  }
`;

const Nav = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${fonts.mono};
  font-size: 12px;
  letter-spacing: 0.1em;
  color: rgba(250, 247, 241, 0.7);

  @media ${media.medium} {
    top: 28px;
    right: 28px;
  }

  button {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid rgba(250, 247, 241, 0.3);
    background: rgba(250, 247, 241, 0.06);
    color: ${colors.linen};
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  button:hover {
    background: rgba(250, 247, 241, 0.16);
    border-color: ${colors.linen};
  }
`;

const Card = styled.article`
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 36px;
  padding: 36px 26px;
  border-radius: 32px;
  background:
    radial-gradient(60% 80% at 0% 0%, rgba(163, 177, 143, 0.22), transparent 60%),
    radial-gradient(50% 60% at 100% 100%, rgba(184, 115, 51, 0.25), transparent 60%),
    ${colors.forest};
  color: ${colors.linen};

  padding-top: 76px;
  touch-action: pan-y;

  @media ${media.medium} {
    padding: 80px 56px 56px;
  }
  @media ${media.large} {
    padding: 88px 64px 64px;
  }
`;

// The card used to carry a column of per-person percentages beside the quote. They
// undersold the stories they sat next to, so the numbers now live only in the pilot
// results section, and the quote runs on its own at a readable measure.
const Body = styled.div`
  max-width: 820px;
`;

const Pull = styled.p`
  font-family: ${fonts.display};
  font-weight: 300;
  font-style: normal;
  font-size: clamp(30px, 3.4vw, 48px);
  line-height: 1.08;
  letter-spacing: -0.025em;
  color: ${colors.copperLight};

  &::before {
    content: "“";
    display: block;
    font-style: normal;
    font-size: 1.6em;
    line-height: 0.5;
    height: 0.4em;
  }
`;

const Quote = styled.blockquote`
  margin-top: 24px;
  display: grid;
  gap: 14px;

  p {
    font-family: ${fonts.body};
    font-size: 18px;
    line-height: 1.6;
    color: rgba(250, 247, 241, 0.85);
  }
`;

const Who = styled.p`
  margin-top: 28px;
  font-family: ${fonts.body};
  font-size: 15px;
  color: rgba(250, 247, 241, 0.65);

  strong {
    display: block;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 22px;
    color: ${colors.linen};
  }
`;

const Disclaimer = styled.p`
  margin-top: 16px;
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${colors.pebble};
`;

const Voices = ({ page, fit = false }) => {
  const scope = useRef(null);
  const people = page ? testimonials.people.filter((t) => t.pages.includes(page)) : testimonials.people;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const touchX = useRef(null);
  const index = Math.min(active, Math.max(0, people.length - 1));
  const person = people[index];
  const count = people.length;
  const autoplay = count > 1 && !paused && inView && !prefersReducedMotion();
  useReveal(scope);

  // Only auto-advance while the section is on screen.
  useEffect(() => {
    const el = scope.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setTimeout(() => setActive((i) => (i + 1) % count), ADVANCE_MS);
    return () => clearTimeout(id);
  }, [autoplay, index, count]);

  const go = (i) => {
    setPaused(true);
    setActive(((i % count) + count) % count);
  };

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1));
  };

  useLayoutEffect(() => {
    if (!person || prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.from("[data-voice-in]", { y: 16, opacity: 0, duration: 0.6, stagger: 0.05, ease: "power3.out" });
    }, scope);
    return () => ctx.revert();
  }, [person]);

  if (!person) return null;

  return (
    <Wrap ref={scope} className={fit ? "fit" : ""} $compact={Boolean(page)} aria-labelledby={`voices-${page || "all"}`}>
      <Container>
        <Head className="head" data-reveal>
          <div>
            <Eyebrow $color={colors.terracotta}>{testimonials.eyebrow}</Eyebrow>
            <Display id={`voices-${page || "all"}`} style={{ marginTop: 18 }}>
              {testimonials.titleLead} <em>{testimonials.titleEmphasis}</em>
            </Display>
          </div>
        </Head>

        <div className="layout">
        {count > 1 && (
          <Selector className="sel" $count={count} role="tablist" aria-label="Choose a story" data-reveal>
            {people.map((p, i) => {
              const on = i === index;
              return (
                <Pick
                  key={p.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  $on={on}
                  onClick={() => go(i)}
                >
                  <span className="top">
                    <b>{p.who}</b>
                    <span className="cue">{on ? "Reading" : "Read story"}</span>
                  </span>
                  <q>{p.pull}</q>
                  <span className="bar" aria-hidden="true">
                    <i key={`${index}-${autoplay}`} className={on ? (autoplay ? "run" : "full") : ""} />
                  </span>
                </Pick>
              );
            })}
          </Selector>
        )}

        <Card
          className="card"
          data-reveal
          role="tabpanel"
          aria-live="polite"
          onMouseEnter={() => setPaused(true)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {count > 1 && (
            <Nav>
              <button type="button" aria-label="Previous story" onClick={() => go(index - 1)}>
                ←
              </button>
              <span>
                {index + 1} / {count}
              </span>
              <button type="button" aria-label="Next story" onClick={() => go(index + 1)}>
                →
              </button>
            </Nav>
          )}
          <Body>
            <Pull className="pull" data-voice-in>{person.pull}</Pull>
            <Quote className="quote">
              {person.quote.map((q) => (
                <p key={q} data-voice-in>
                  {q}
                </p>
              ))}
            </Quote>
            <Who className="who" data-voice-in>
              <strong>{person.who}</strong>
              {person.detail}
            </Who>
          </Body>
        </Card>
        </div>
        <Disclaimer>{testimonials.disclaimer}</Disclaimer>
      </Container>
    </Wrap>
  );
};

export default Voices;
